import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ErrorBanner, Spinner } from '../../../Admin/shared';
import { useUserReferrals } from '../api/useUserReferrals';
import type { InvitedRow } from '../api/types';
import { DateRangeControl } from '@/components/shared';
import { ENTERED_VIA_LABELS, STATUS_LABELS } from '../components/ReferralFilters';

function daysAgoInput(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

/** `—` for a value we could not compute. Never `0` — that is a different claim. */
function fmtNum(v: number | null): string {
  return v === null ? '—' : v.toLocaleString();
}

/** `owed_cents` is CENTS on the wire — divide before formatting as currency. */
function fmtCents(v: number | null): string {
  if (v === null) return '—';
  return (v / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

/** A `counted_at` that fails to parse renders `—`, never "Invalid Date". */
function fmtWhen(v: string): string {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/**
 * `display_name` and `user_id` can both be null on an invited row (deleted
 * account) — fall back gracefully, never render the literal string "null" or
 * a blank cell.
 */
function invitedLabel(row: { display_name: string | null; user_id: string | null }): string {
  return row.display_name ?? row.user_id ?? 'deleted account';
}

/**
 * A null `invite_channel` means the channel was not recorded — distinct from
 * "no source." Settings-entered referrals always have a null channel by
 * design (that redemption path never captures one), so those rows are
 * labeled distinctly rather than reading as a data-quality gap. Matches
 * `ReferralList`'s `channelCell` so the two views never drift.
 */
function channelCell(row: InvitedRow): string {
  if (row.invite_channel !== null) return row.invite_channel;
  return row.entered_via === 'settings' ? 'Not recorded (settings entry)' : 'Not recorded';
}

function entryLabel(entered_via: 'signup' | 'settings'): string {
  return ENTERED_VIA_LABELS[entered_via] ?? entered_via;
}

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 px-5 py-4">
      <p className="text-[11px] text-gray-500 font-medium">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-white tabular-nums tracking-tight">{value}</p>
      {hint && <p className="mt-1.5 text-[11px] text-gray-500">{hint}</p>}
    </div>
  );
}

/**
 * Per-user referral drill-down: this user's referral codes and their four
 * headline stats, then the full outbound history of who they invited, and
 * finally who (if anyone) referred them in.
 *
 * The windowing is deliberately asymmetric and this page must not blur it:
 * `invited` is the user's WHOLE outbound history and does not move with the
 * date range — only `stats.invited_in_range` does. `referred_by` is never
 * windowed at all. The stat labels below call this out explicitly so a
 * reader can't mistake the lifetime figures for in-range ones.
 */
export function UserReferralsPage(): React.ReactElement {
  const { userId } = useParams<{ userId: string }>();
  const [from, setFrom] = useState<string | null>(daysAgoInput(29));
  const [to, setTo] = useState<string | null>(todayInput());

  const { data, loading, error } = useUserReferrals(userId ?? '', {
    from: from ?? undefined,
    to: to ?? undefined,
  });

  const viewedUserLabel = data ? (data.user.display_name ?? data.user.email ?? data.user.user_id) : (userId ?? '');

  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-5">
        <div>
          <Link
            to="/analytics/referrals"
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            ← Back to referral analytics
          </Link>
        </div>

        <header className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold text-white tracking-tight">{viewedUserLabel}</h1>
            {data?.user.email && data.user.display_name && (
              <p className="mt-1 text-xs text-gray-500">{data.user.email}</p>
            )}
            {!data && !loading && <p className="mt-1 text-xs text-gray-500">User</p>}
            {data && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {data.codes.length === 0 ? (
                  <span className="text-[11px] text-gray-600">No referral codes on record.</span>
                ) : (
                  data.codes.map((c) => (
                    <span
                      key={c.code}
                      title={`${c.kind} · ${c.status}`}
                      className="px-2 py-0.5 rounded-md text-[11px] font-mono bg-gray-900 border border-gray-700 text-gray-300"
                    >
                      {c.code}
                    </span>
                  ))
                )}
              </div>
            )}
          </div>
          <DateRangeControl
            from={from}
            to={to}
            onChange={(f, t) => {
              setFrom(f);
              setTo(t);
            }}
          />
        </header>

        {error && <ErrorBanner message={error} />}
        {loading && !data && (
          <div className="flex justify-center py-24">
            <Spinner />
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat
                label="Invited (lifetime)"
                value={fmtNum(data.stats.invited_total)}
                hint="all-time total — ignores the date range"
              />
              <Stat
                label="Verified (lifetime)"
                value={fmtNum(data.stats.invited_verified)}
                hint="all-time total — ignores the date range"
              />
              <Stat
                label="Invited in period"
                value={fmtNum(data.stats.invited_in_range)}
                hint="the only stat that moves with the range"
              />
              <Stat
                label="Owed (lifetime)"
                value={fmtCents(data.stats.owed_cents)}
                hint="all-time total — ignores the date range"
              />
            </div>

            <section className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-700">
                <h2 className="text-sm font-semibold text-gray-200">They invited</h2>
                <p className="mt-1 text-[11px] text-gray-500">
                  This user&apos;s entire outbound referral history, most recent first — not affected
                  by the date range above.
                </p>
              </div>

              {data.invited_truncated && (
                <div className="mx-5 mt-4 flex items-start gap-2 p-3 bg-amber-950/40 border border-amber-800/50 rounded-xl">
                  <svg
                    className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m0 3.75h.008M10.29 3.86l-8.18 14.16A1.5 1.5 0 003.42 20.5h17.16a1.5 1.5 0 001.31-2.48L13.71 3.86a1.5 1.5 0 00-2.42 0z"
                    />
                  </svg>
                  <p className="text-sm text-amber-300">
                    Only the most recent 500 people this user invited are listed.
                  </p>
                </div>
              )}

              {data.invited.length === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-gray-500">
                  This user has not invited anyone on record.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                          When
                        </th>
                        <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                          Who
                        </th>
                        <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                          Code
                        </th>
                        <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                          Entry
                        </th>
                        <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                          Channel
                        </th>
                        <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.invited.map((row, i) => (
                        <tr
                          key={`${row.user_id ?? 'deleted'}-${row.counted_at}-${i}`}
                          className="border-b border-gray-800 last:border-b-0 hover:bg-gray-800/40 transition-colors"
                        >
                          <td className="px-5 py-3 text-gray-400 whitespace-nowrap">{fmtWhen(row.counted_at)}</td>
                          <td className="px-5 py-3">
                            {row.user_id ? (
                              <Link
                                to={`/analytics/referrals/users/${row.user_id}`}
                                className="text-gray-200 hover:text-violet-300 transition-colors font-medium"
                              >
                                {invitedLabel(row)}
                              </Link>
                            ) : (
                              <span className="italic text-gray-500">{invitedLabel(row)}</span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-gray-400">{row.code ?? '—'}</td>
                          <td className="px-5 py-3 text-gray-400">{entryLabel(row.entered_via)}</td>
                          <td className="px-5 py-3 text-gray-400">{channelCell(row)}</td>
                          <td className="px-5 py-3 text-gray-400">{statusLabel(row.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-700">
                <h2 className="text-sm font-semibold text-gray-200">Referred by</h2>
                <p className="mt-1 text-[11px] text-gray-500">
                  Who brought this user in, if anyone — never affected by the date range above.
                </p>
              </div>

              {data.referred_by === null ? (
                <p className="px-5 py-10 text-center text-sm text-gray-500">Nobody referred them.</p>
              ) : (
                <div className="px-5 py-4 flex items-center justify-between gap-4 flex-wrap text-xs">
                  <div>
                    <p className="text-[11px] text-gray-500 font-medium">Referrer</p>
                    {data.referred_by.user_id ? (
                      <Link
                        to={`/analytics/referrals/users/${data.referred_by.user_id}`}
                        className="mt-1 block text-gray-200 hover:text-violet-300 transition-colors font-medium"
                      >
                        {invitedLabel(data.referred_by)}
                      </Link>
                    ) : (
                      <p className="mt-1 italic text-gray-500">{invitedLabel(data.referred_by)}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-500 font-medium">Code</p>
                    <p className="mt-1 text-gray-400">{data.referred_by.code ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-500 font-medium">Entry</p>
                    <p className="mt-1 text-gray-400">{entryLabel(data.referred_by.entered_via)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-500 font-medium">When</p>
                    <p className="mt-1 text-gray-400 whitespace-nowrap">{fmtWhen(data.referred_by.counted_at)}</p>
                  </div>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default UserReferralsPage;
